/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import ReplayIcon from "@material-ui/icons/Replay";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import arrow from "designs/images/streaming/arrow_down.png";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState, useEffect } from "react";
import { useSelector } from "store";
import { LiveCommerceListItem } from "ui/Streaming/LiveCommerce/LiveCommerceListItem";
import {
  createListSetOfField,
  isDisplayPeriod,
  convertMapToValues,
} from "utility";

import type { ProductData } from "@spwn/types/firebase/firestore";
import type { Store } from "store";

type Props = {
  THEOPlayerElementHeight: number;
  pseudoCart: (ProductData & { count: number })[];
  selectedLiveCommerceGoods: ProductData[];
  setType: (type: string) => void;
  addToCart: (classGoodsList: ProductData[]) => void;
  addToCartToBuy: (classGoodsList: ProductData[]) => void;
  openGoodsInfo: (goodsItem: ProductData) => void;
  openLiveCommerceCart: () => void;
  incrementCartItem: (itemData: ProductData) => void;
  decrementCartItem: (itemData: ProductData) => void;
};
const StreamingLiveCommerceGoodsListV2: React.FC<Props> = (props) => {
  const [ticketsCategoryList, setTicketsCategoryList] = useState<ProductData[]>(
    []
  );
  const [goodsCategoryList, setGoodsCategoryList] = useState<ProductData[]>([]);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const displayEvent = useSelector((state: Store) => state.event.displayEvent);
  const eventTicketMap = useSelector(
    (state: Store) => state.ticket.eventTicketMap
  );

  const classesLiveCommerceDesign = liveCommerceDesign({
    isDarkModeEnabled,
    THEOPlayerElementHeight: props.THEOPlayerElementHeight,
  });

  const didMount = () => {
    setSellingItemsList();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(didMount, []); // TODO

  const selectedLiveCommerceTickets = convertMapToValues(eventTicketMap).filter(
    (ticket) =>
      ticket?.saleType &&
      (ticket?.saleType === "liveCommerce" ||
        ticket?.saleType === "liveCommerceOnly") &&
      isDisplayPeriod(
        ticket.display,
        ticket.releaseDateTime,
        ticket.closeDateTime
      )
  );

  const setSellingItemsList = () => {
    setGoodsCategoryList(
      createListSetOfField<ProductData>(
        props.selectedLiveCommerceGoods,
        "class"
      )
    );
    setTicketsCategoryList(selectedLiveCommerceTickets);
  };

  return (
    <StreamingLiveCommerceGoodsListV2View
      isDarkModeEnabled={isDarkModeEnabled}
      ticketsCategoryList={ticketsCategoryList}
      goodsCategoryList={goodsCategoryList}
      selectedLiveCommerceGoods={props.selectedLiveCommerceGoods}
      displayEvent={displayEvent}
      setType={props.setType}
      addToCart={props.addToCart}
      addToCartToBuy={props.addToCartToBuy}
      openGoodsInfo={props.openGoodsInfo}
      setSellingItemsList={setSellingItemsList}
      classesLiveCommerceDesign={classesLiveCommerceDesign}
    />
  );
};

type StreamingLiveCommerceGoodsListV2ViewProps = {
  isDarkModeEnabled: boolean;
  ticketsCategoryList: ProductData[];
  goodsCategoryList: ProductData[];
  selectedLiveCommerceGoods: ProductData[];
  displayEvent: Store["event"]["displayEvent"];
  setType: (type: string) => void;
  addToCart: (classGoodsList: ProductData[]) => void;
  addToCartToBuy: (classGoodsList: ProductData[]) => void;
  openGoodsInfo: (goodsItem: ProductData) => void;
  setSellingItemsList: () => void;
  classesLiveCommerceDesign: ReturnType<typeof liveCommerceDesign>;
};

const StreamingLiveCommerceGoodsListV2View: React.FC<
  StreamingLiveCommerceGoodsListV2ViewProps
> = ({
  isDarkModeEnabled,
  ticketsCategoryList,
  goodsCategoryList,
  selectedLiveCommerceGoods,
  displayEvent,
  setType,
  addToCart,
  addToCartToBuy,
  openGoodsInfo,
  setSellingItemsList,
  classesLiveCommerceDesign,
}) => {
  const { t } = useI18n();

  return (
    <div css={classesLiveCommerceDesign.root}>
      <div css={classesLiveCommerceDesign.list}>
        {ticketsCategoryList.length > 0 && (
          <React.Fragment>
            <div css={classesLiveCommerceDesign.header}>
              <p>TICKET</p>
            </div>
            <div css={classesLiveCommerceDesign.item}>
              <div css={classesLiveCommerceDesign.ticketImage}>
                <img src={displayEvent.thumbnail} alt={displayEvent.title} />
              </div>
              <div css={classesLiveCommerceDesign.text}>
                <h3>{displayEvent.title}</h3>
              </div>
            </div>
            {ticketsCategoryList.map((ticket, index) => {
              const classTicketList = [ticket];
              return (
                <div css={classesLiveCommerceDesign.ticketItem} key={index}>
                  <h3>{ticket.name}</h3>
                  <p css={classesLiveCommerceDesign.price}>
                    ¥{ticket.price_jpy.toLocaleString()}
                    <span>{t("streaming.commerceGoodsList.tax")}</span>
                  </p>
                  <div css={classesLiveCommerceDesign.ticketButtonList}>
                    <p
                      css={classesLiveCommerceDesign.cart}
                      onClick={() => addToCart(classTicketList)}
                    >
                      <ShoppingCartIcon />
                    </p>
                    <p
                      css={classesLiveCommerceDesign.purchase}
                      onClick={() => addToCartToBuy(classTicketList)}
                    >
                      {t("streaming.commerceGoodsList.purchase")}
                    </p>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        )}

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          {goodsCategoryList.length > 0 && (
            <React.Fragment>
              {goodsCategoryList.map((item) => {
                const classGoodsList = selectedLiveCommerceGoods.filter(
                  (el) => el.className === item.name
                );

                return (
                  <LiveCommerceListItem
                    key={item._id}
                    isDarkModeEnabled={isDarkModeEnabled}
                    item={item}
                    goodsVariationList={classGoodsList}
                    openGoodsInfo={openGoodsInfo}
                    addToCart={addToCart}
                    selectVariation={setType}
                  />
                );
              })}
            </React.Fragment>
          )}
        </div>

        {ticketsCategoryList.length === 0 && goodsCategoryList.length === 0 && (
          <p
            css={classesLiveCommerceDesign.replay}
            onClick={setSellingItemsList}
          >
            <ReplayIcon />
          </p>
        )}
      </div>
    </div>
  );
};

type LiveCommerceDesignProps = {
  isDarkModeEnabled: boolean;
  THEOPlayerElementHeight?: number;
};
const liveCommerceDesign = (props: LiveCommerceDesignProps) => {
  const textColorInDarkMode = props.isDarkModeEnabled
    ? css`
        color: #fff;
      `
    : null;
  const borderColorInDarkMode = props.isDarkModeEnabled
    ? css`
        border: 1px solid #fff;
      `
    : null;

  const root = css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `;

  const lightHeaderColor = css`
    background-color: #f5f5f5;
    color: #000;
  `;

  const darkHeaderColor = css`
    background-color: #333333;
    color: #fff;
  `;

  const headerBase = css`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 12px 16px;
    border-radius: 8px;
    p {
      padding: 0 12px;
      font-size: 12px;
    }
  `;

  const header = css`
    ${headerBase}
    ${props.isDarkModeEnabled ? darkHeaderColor : lightHeaderColor}
  `;

  const list = css`
    flex: 1 1 auto;
    width: 100%;
    height: 120px;
    padding-bottom: 16px;
    overflow-y: auto;
  `;
  const item = css`
    display: flex;
  `;
  const ticketItem = css`
    padding: 10px;
    h3 {
      margin-bottom: 8px;
      ${textColorInDarkMode}
      font-size: 16px;
      font-weight: normal;
      line-height: 1.3em;
    }
  `;
  const image = css`
    position: relative;
    width: 150px;
    height: 150px;
    margin-right: 10px;
    object-fit: cover;
    img {
      width: 100%;
    }
    p {
      position: absolute;
      top: 4px;
      left: 4px;
      background-color: #fff;
      border-radius: 50%;
      cursor: pointer;
      svg {
        fill: #bbb;
      }
    }
  `;
  const ticketImage = css`
    width: 150px;
    margin-right: 10px;
    img {
      width: 100%;
    }
  `;
  const text = css`
    width: calc(100% - 160px);
    height: 100%;
    h3 {
      margin-bottom: 16px;
      ${textColorInDarkMode}
      font-size: 14px;
      font-weight: normal;
      line-height: 1.3em;
    }
  `;
  const select = css`
    width: 100%;
    padding: 5px 8px;
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: normal;
    background-color: #f4f4f4;
    background-image: url(${arrow});
    background-size: 8px auto;
    background-repeat: no-repeat;
    background-position: 95% center;
    border: 1px solid #dedede;
    border-radius: 5px;
    appearance: none;
  `;
  const replay = css`
    margin: 100px 40%;
    text-align: center;
    cursor: pointer;
    &:active {
      opacity: 0.6;
    }
  `;
  const price = css`
    margin-bottom: 10px;
    ${textColorInDarkMode}
    font-size: 14px;
    span {
      margin-left: 4px;
      font-size: 12px;
    }
  `;
  const buttonList = css`
    display: flex;
    p {
      background-color: #00c2ae;
      border-radius: 5px;
    }
  `;
  const purchaseButton = props.isDarkModeEnabled
    ? css`
        background-color: #000;
        border-radius: 6px;
        padding: 12px;
        p {
          color: #fff;
        }
      `
    : css`
        background-color: #fff;
        border-radius: 6px;
        padding: 12px;
        border: 1px solid #000;
        p {
          color: #000;
        }
      `;
  const ticketButtonList = css`
    display: flex;
    width: calc(100% - 160px);
    margin-left: auto;
    p {
      background-color: #00c2ae;
      border-radius: 5px;
    }
  `;
  const cartInButton = props.isDarkModeEnabled
    ? css`
        background-color: #000;
        border-radius: 6px;
        border: 1px solid #fff;
        padding: 8px 12px;
        text-align: center;
        cursor: pointer;
      `
    : css`
        background-color: #fff;
        border-radius: 6px;
        border: 1px solid #000;
        padding: 8px 12px;
        text-align: center;
        cursor: pointer;
      `;

  const cart = props.isDarkModeEnabled
    ? css`
        margin: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        line-height: 1;
        color: #fff;
        font-size: 14px;
        svg {
          width: 16px;
          height: 16px;
          fill: #fff;
        }
      `
    : css`
        margin: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        line-height: 1;
        color: #000;
        font-size: 14px;
        svg {
          width: 16px;
          height: 16px;
          fill: #000;
        }
      `;
  const purchase = css`
    padding: 10px;
    color: #fff;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    cursor: pointer;
  `;
  const ItemsInYourCart = css`
    position: fixed;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px 24px;
    background-color: #202020;
    ${borderColorInDarkMode}
    @media screen and (min-width: 768px) {
      position: absolute;
      bottom: 0;
    }
    p {
      color: #fff;
      font-size: 14px;
    }
    svg {
      fill: #fff;
      cursor: pointer;
    }
  `;
  const addressRegistration = css`
    position: fixed;
    bottom: 0;
    width: 100%;
    background-color: #202020;
    ${borderColorInDarkMode}
    @media screen and (min-width: 768px) {
      position: absolute;
      bottom: 0;
    }
    a {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;
    }
    p {
      color: #fff;
      font-size: 14px;
      line-height: 1.3em;
      span {
        font-size: 12px;
      }
    }
    svg {
      fill: #fff;
    }
  `;
  return {
    root,
    header,
    list,
    item,
    ticketItem,
    image,
    ticketImage,
    text,
    select,
    replay,
    price,
    buttonList,
    ticketButtonList,
    purchaseButton,
    cart,
    cartInButton,
    purchase,
    ItemsInYourCart,
    addressRegistration,
  };
};
export default StreamingLiveCommerceGoodsListV2;
